import React, { useState, useEffect, useCallback } from 'react';
import { Tab, Tabs, TabList, TabPanel } from 'react-tabs';
import 'react-tabs/style/react-tabs.css';
import { useParams } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import api from './services/api';
import CreaEvento from './CreaEvento';
import EventList from './GestioneEventi';
import ProfileDetailsForm from './components/profile/ProfileDetailsForm';
import ProfileImageTab from './components/profile/ProfileImageTab';
import GalleriaTab from './components/profile/GalleriaTab';

const INITIAL_AZIENDA = {
  idAzienda: null,
  nomeAzienda: '',
  comune: '',
  indirizzo: '',
  descrizione: '',
  longitudine: '',
  latitudine: '',
  tipologie: '',
  partitaIva: '',
  iban: '',
  immagineProfilo: null,
  galleria: [],
};

const ProfilePageAdmin = () => {
  const { id } = useParams();

  const [azienda, setAzienda] = useState(INITIAL_AZIENDA);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [profileImageSrc, setProfileImageSrc] = useState(null);

  const [galleria, setGalleria] = useState([]);
  const [loadingGalleria, setLoadingGalleria] = useState(false);
  const [errorGalleria, setErrorGalleria] = useState(null);

  useEffect(() => {
    if (!id) return undefined;
    const controller = new AbortController();
    (async () => {
      try {
        const response = await api.getAziendaByUser(id);
        if (!controller.signal.aborted) setAzienda(response.data);
      } catch (err) {
        if (err.name !== 'CanceledError' && err.name !== 'AbortError') {
          setError('Errore nel recupero dei dati');
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    })();
    return () => controller.abort();
  }, [id]);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setAzienda((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await api.updateAziendaByUser(id, azienda);
      toast.success('Dati aggiornati con successo!');
    } catch {
      toast.error("Errore durante l'invio dei dati");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleProfileImageUpload = async (file) => {
    try {
      const response = await api.uploadProfileImageByUser(id, file);
      setAzienda((prev) => ({
        ...prev,
        immagineProfilo: response.data.url,
      }));
      toast.success('Immagine profilo caricata');
    } catch {
      toast.error("Errore durante il caricamento dell'immagine");
    }
  };

  const caricaImmagineProfilo = useCallback(async () => {
    try {
      const response = await api.getProfileImageByUser(id);
      setProfileImageSrc(response.data);
    } catch {
      toast.error("Errore nel caricamento dell'immagine profilo");
    }
  }, [id]);

  const caricaGalleria = useCallback(async () => {
    setLoadingGalleria(true);
    setErrorGalleria(null);
    try {
      const response = await api.getGalleriaByUser(id);
      setGalleria(response.data);
    } catch {
      setErrorGalleria('Errore nel caricamento della galleria');
    } finally {
      setLoadingGalleria(false);
    }
  }, [id]);

  const handleGalleryUpload = async (files) => {
    try {
      await api.uploadGalleryByUser(id, files);
      toast.success('Immagini caricate');
      caricaGalleria();
    } catch {
      toast.error('Errore durante il caricamento della galleria');
    }
  };

  const handleDeleteImage = async (idImmagine) => {
    try {
      await api.deleteImmagine(idImmagine);
      caricaGalleria();
    } catch {
      toast.error("Errore durante l'eliminazione dell'immagine");
    }
  };

  if (loading) return <div>Caricamento...</div>;
  if (error) return <div>{error}</div>;

  return (
    <>
      <Tabs
        onSelect={(index) => {
          if (index === 1) caricaImmagineProfilo();
          if (index === 2) caricaGalleria();
        }}
      >
        <TabList>
          <Tab>Dettagli Profilo</Tab>
          <Tab>Immagine Profilo</Tab>
          <Tab>Galleria</Tab>
          <Tab>Crea offerta</Tab>
          <Tab>Gestisci offerta</Tab>
        </TabList>

        <TabPanel>
          <ProfileDetailsForm
            azienda={azienda}
            onChange={handleChange}
            onSubmit={handleSubmit}
            useComuniSelect
            isSubmitting={isSubmitting}
          />
        </TabPanel>

        <TabPanel>
          <ProfileImageTab
            imageSrc={profileImageSrc}
            onUpload={handleProfileImageUpload}
          />
        </TabPanel>

        <TabPanel>
          <GalleriaTab
            galleria={galleria}
            loading={loadingGalleria}
            error={errorGalleria}
            onUpload={handleGalleryUpload}
            onDelete={handleDeleteImage}
          />
        </TabPanel>

        <TabPanel>
          <CreaEvento idAzienda={azienda.idAzienda} />
        </TabPanel>

        <TabPanel>
          <EventList idAzienda={azienda.idAzienda} />
        </TabPanel>
      </Tabs>
      <ToastContainer />
    </>
  );
};

export default ProfilePageAdmin;
